import React, { useState } from "react";
import Link from "next/link";
import classNames from "classnames";
import { useRouter } from "next/router";
import { imageSizerString } from "lib/fun";
import { Icon } from "components/atoms/icon";
import config from "config";
import { getArticlePath } from "common";

import { wrap } from "../../lib/hoc";
import {
  PageLayout,
  scrollPageLayoutTo,
} from "../../components/molecules/pageLayout";
import styles from "./PageListArticles.module.scss";
import { OneColumn } from "../../components/atoms/oneColumn";
import { Breadcrumbs } from "../../components/molecules/breadcrumb";
import {
  MobileFilterItem,
  MobileFiltersPanel,
} from "../../components/molecules/mobileFiltersPanel";
import { Button } from "../../components/atoms/button";
import {
  DesktopFilterGroup,
  DesktopFilterItem,
  DesktopFilters,
} from "../../components/molecules/desktopFilter";
import { useDevice } from "../../contexts/deviceContext";
import {
  ArticleCategoryItemFragment,
  ArticleItemFragment,
  ListFeaturedArticlesQuery,
} from "../../generated/graphql";
import { Routes } from "../../generated/routes";
import { ArticleSubHeader } from "./ArticleSubHeader";
import noResultsMobile from "../../../assets/images/noResultsMobile.png";
import noResultsDesktop from "../../../assets/images/noResultsDesktop.png";
import { ArticleCard } from "../../components/molecules/articleCard/ArticleCard";
import { useForm } from "../../form";
import { DropDown, withNullOption } from "../../components/molecules/dropDown";
import { TextInput } from "../../components/molecules/textInput";
import { Pager } from "../../components/molecules/pager";
import { RankedArticles } from "./RankedArticles";

const ITEM_PER_PAGE = 30;

type ListArticleFilterForm = {
  category: ArticleCategoryItemFragment | null;
  title: string;
};

interface Props {
  initFilter: ListArticleFilterForm;
  categories: Array<ArticleCategoryItemFragment>;
  page: number;
  totalCount: number;
  articles: Array<ArticleItemFragment>;
  featuredArticles: ListFeaturedArticlesQuery["featuredArticles"];
  rankedArticles: Array<ArticleItemFragment>;
}

const PageListArticles: React.FC<Props> = ({
  articles,
  featuredArticles,
  rankedArticles,
  initFilter,
  categories,
  page,
  totalCount,
}) => {
  const device = useDevice();
  const isMobile = device === "mobile";
  const pages = Math.ceil(totalCount / ITEM_PER_PAGE);
  const [isLoadingNew, setIsLoadingNew] = useState(false);
  const form = useForm<ListArticleFilterForm>({
    initValue: initFilter,
    validators: {},
  });
  const { field, formValues } = form;
  const router = useRouter();
  const clearFields = () => {
    field("title").setValue("");
    field("category").setValue(null);
  };
  const makeQuery = (formValues: ListArticleFilterForm) => ({
    categoryId: formValues.category ? formValues.category.id : undefined,
    title: formValues.title,
  });
  const onSubmit = async () => {
    setIsLoadingNew(true);
    await router.push(
      Routes.url("/articles/page/[page]", {
        page: 1,
        ...makeQuery(formValues),
      }),
    );
    setIsLoadingNew(false);
  };
  const goToPage = async (page: number) => {
    setIsLoadingNew(true);
    scrollPageLayoutTo({
      top: 0,
      behavior: "smooth",
    });
    await router.push(
      Routes.url("/articles/page/[page]", {
        page,
        ...makeQuery(formValues),
      }),
    );
    setIsLoadingNew(false);
  };

  return (
    <PageLayout
      title={
        initFilter.category
          ? "「" + initFilter.category.name + "」の記事一覧 | en-courage"
          : "【 就活に役立つ記事が盛り沢山！ 】ES・選考体験記から就活コラム、企業インタビューまで | en-courage"
      }
      metaDescription={
        initFilter.category?.description
          ? initFilter.category.description
          : "en-courageが厳選した就活に役立つコンテンツの一覧です。内定者のES/選考体験記から、面接・業界研究ができる就活コラム、そして人気企業へのインタビュー記事など、幅広いコンテンツをご用意しています。"
      }
    >
      <div className={styles.main}>
        <OneColumn>
          <Breadcrumbs
            breadcrumbs={[
              {
                label: "記事一覧",
                home: true,
                to: Routes.url("/articles/page/[page]", { page: 1 }),
              },
            ]}
          />
          {isMobile ? (
            <div className={styles.filters}>
              <MobileFiltersPanel openMode="always">
                <div className={styles.filtersContainer}>
                  <MobileFilterItem hasBorder className={styles.textSearch}>
                    <TextInput
                      {...field("title")}
                      label="記事タイトル"
                      headless
                      className={styles.filterInput}
                      type="arrange"
                    />
                    <Icon name="search" size="large" />
                  </MobileFilterItem>
                  <MobileFilterItem hasBorder>
                    <DropDown
                      {...field("category")}
                      headless
                      {...withNullOption(
                        {
                          options: categories.sort(
                            (c1, c2) =>
                              (c1.displayOrder || 0) - (c2.displayOrder || 0),
                          ),
                          toStr: (c) => `${c.id}`,
                          fromStr: (str) =>
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            categories.find((c) => `${c.id}` === str)!,
                          renderLabel: (c) => c.name,
                        },
                        "カテゴリー（全て）",
                      )}
                      type="arrange"
                    />
                  </MobileFilterItem>
                </div>
              </MobileFiltersPanel>
              <div className={styles.filtersButton}>
                <Button
                  size="midSmall"
                  color="secondary"
                  onClick={clearFields}
                  className={styles.clearFields}
                >
                  クリア
                </Button>
                <Button size="midSmall" onClick={onSubmit}>
                  絞り込む
                </Button>
              </div>
            </div>
          ) : (
            <div className={styles.filters}>
              <DesktopFilters>
                <DesktopFilterGroup>
                  <DesktopFilterItem filterLabel="カテゴリー" halfWidth>
                    <DropDown
                      {...field("category")}
                      {...withNullOption(
                        {
                          options: categories.sort(
                            (c1, c2) =>
                              (c1.displayOrder || 0) - (c2.displayOrder || 0),
                          ),
                          toStr: (c) => `${c.id}`,
                          fromStr: (str) =>
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            categories.find((c) => `${c.id}` === str)!,
                          renderLabel: (c) => c.name,
                        },
                        "全て",
                      )}
                      type="arrange"
                    />
                  </DesktopFilterItem>
                  <DesktopFilterItem
                    filterLabel="タイトル"
                    halfWidth
                    withClearButton
                    clearButtonClick={clearFields}
                  >
                    <TextInput
                      {...field("title")}
                      className={styles.filterInput}
                      type="arrange"
                    />
                  </DesktopFilterItem>
                </DesktopFilterGroup>
              </DesktopFilters>
              <div className={styles.filtersButton}>
                <Button
                  className={styles.clearFields}
                  size="midSmall"
                  color="secondary"
                  onClick={clearFields}
                >
                  クリア
                </Button>
                <Button size="midSmall" onClick={onSubmit}>
                  絞り込む
                </Button>
              </div>
            </div>
          )}
          <div className={styles.container}>
            <div className={styles.leftColumn}>
              <ArticleSubHeader label="Latest articles" subLabel="新着記事" />
              <div className={styles.latestArticles}>
                {articles.length === 0 ? (
                  <div className={styles.noResult}>
                    <img src={isMobile ? noResultsMobile : noResultsDesktop} />
                    <div className={styles.noResultTexts}>
                      <div className={styles.noResultTitle}>
                        ご指定の条件に該当する検索結果が見つかりませんでした
                      </div>
                      <div className={styles.noResultSubTitle}>
                        検索条件を変更して再度検索してください。
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {articles.map((article) => (
                      <div
                        key={article.id}
                        className={classNames(styles.latestArticle, {
                          [styles.latestArticleIsLoading]: isLoadingNew,
                        })}
                      >
                        <ArticleCard article={article} />
                      </div>
                    ))}
                    <div className={styles.pager}>
                      <Pager
                        onChange={goToPage}
                        currentPage={page}
                        pages={pages}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className={styles.rightColumn}>
              {featuredArticles.length ? (
                <div className={styles.articles}>
                  <ArticleSubHeader
                    label="Special articles"
                    subLabel="特集記事"
                  />
                  <div>
                    {featuredArticles.map((fa) => (
                      <div className={styles.article} key={fa.id}>
                        <Link href={getArticlePath(String(fa.article.id))}>
                          <a>
                            <div className={styles.articleImg}>
                              <img
                                src={`${imageSizerString(
                                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                  fa.article.imageUrl!,
                                  500,
                                  250,
                                )}`}
                              />
                            </div>
                            <div className={styles.articleLink}>
                              {fa.article.title}
                            </div>
                            <div className={styles.articleCategory}>
                              {fa.article.category.name}
                            </div>
                          </a>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              <RankedArticles rankedArticles={rankedArticles} />
            </div>
          </div>
        </OneColumn>
      </div>
    </PageLayout>
  );
};
export default wrap(PageListArticles, async () => {
  return {
    status: "redirectTo",
    permanent: true,
    path: `${config.BAKLAVA_URL}/articles`,
  };
});
